/**
 * API-specific type guards and response validators
 *
 * These utilities ensure API responses match expected shapes and provide
 * safe defaults for paginated responses.
 */

import { isPlainObject, isNumber } from '../utils/guards';
import type {
  Permit,
  PermitsResponse,
  JobDiversionStats,
  MaterialSummary,
  JobSummaryResponse,
} from '@/types';

/**
 * Generic paginated response interface
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Type guard for paginated response structure
 * Validates that response has required pagination fields
 */
export function isPaginatedResponse<T>(
  value: unknown,
  itemsKey: string = 'items'
): value is PaginatedResponse<T> {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasItems = Array.isArray((value as any)[itemsKey]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasTotal = isNumber((value as any).total);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasLimit = isNumber((value as any).limit);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasOffset = isNumber((value as any).offset);

  return hasItems && hasTotal && hasLimit && hasOffset;
}

/**
 * Ensures a value is a valid paginated response with safe defaults
 * If validation fails, returns empty response with defaults
 */
export function ensurePaginatedResponse<T>(
  value: unknown,
  itemsKey: string = 'items',
  defaultLimit: number = 10
): PaginatedResponse<T> {
  const defaultResponse: PaginatedResponse<T> = {
    items: [],
    total: 0,
    limit: defaultLimit,
    offset: 0,
  };

  if (!isPaginatedResponse<T>(value, itemsKey)) {
    console.warn('Invalid paginated response structure, using defaults:', value);
    return defaultResponse;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items = (value as any)[itemsKey] || [];

  return {
    items,
    total: value.total >= 0 ? value.total : 0,
    limit: value.limit > 0 ? value.limit : defaultLimit,
    offset: value.offset >= 0 ? value.offset : 0,
  };
}

/**
 * Type guard for success responses with boolean success field
 */
export function isSuccessResponse(
  value: unknown
): value is { success: boolean } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return isPlainObject(value) && typeof (value as any).success === 'boolean';
}

/**
 * Ensure success response with safe default
 */
export function ensureSuccessResponse(value: unknown): { success: boolean } {
  if (!isSuccessResponse(value)) {
    console.warn('Invalid success response, using default:', value);
    return { success: false };
  }
  return value;
}

/**
 * Type guard for error responses
 */
export function isApiError(
  value: unknown
): value is { error: string; message?: string } {
  if (!isPlainObject(value)) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof (value as any).error === 'string';
}

/**
 * Extract error message from various error response formats
 */
export function extractErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message || error.error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (isPlainObject(error) && typeof (error as any).message === 'string') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error as any).message;
  }

  return 'An unknown error occurred';
}

/**
 * Type guard for Permit
 */
export function isPermit(value: unknown): value is Permit {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permit = value as any;
  return (
    typeof permit.id === 'string' &&
    typeof permit.job_id === 'string' &&
    typeof permit.permit_number === 'string'
  );
}

/**
 * Type guard for PermitsResponse
 */
export function isPermitsResponse(value: unknown): value is PermitsResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.permits);
}

/**
 * Ensures a value is a valid PermitsResponse with safe defaults
 */
export function ensurePermitsResponse(value: unknown): PermitsResponse {
  const defaultResponse: PermitsResponse = {
    permits: [],
  };

  if (!isPermitsResponse(value)) {
    console.warn('Invalid PermitsResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    permits: Array.isArray(value.permits) ? value.permits.filter(isPermit) : [],
  };
}

/**
 * Ensure Permit with validation
 */
export function ensurePermit(value: unknown): Permit {
  if (!isPermit(value)) {
    console.warn('Invalid Permit:', value);
    throw new Error('Invalid permit response');
  }
  return value;
}

/**
 * Type guard for JobDiversionStats
 */
export function isJobDiversionStats(value: unknown): value is JobDiversionStats {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stats = value as any;
  return (
    isNumber(stats.total_weight) &&
    isNumber(stats.diverted_weight) &&
    isNumber(stats.landfill_weight) &&
    isNumber(stats.diversion_rate) &&
    isNumber(stats.target_diversion_rate)
  );
}

/**
 * Ensures a value is valid JobDiversionStats with safe defaults
 */
export function ensureJobDiversionStats(value: unknown): JobDiversionStats {
  const defaultStats: JobDiversionStats = {
    total_weight: 0,
    diverted_weight: 0,
    landfill_weight: 0,
    diversion_rate: 0,
    target_diversion_rate: 0,
  };

  if (!isJobDiversionStats(value)) {
    console.warn('Invalid JobDiversionStats, using defaults:', value);
    return defaultStats;
  }

  return {
    total_weight: value.total_weight >= 0 ? value.total_weight : 0,
    diverted_weight: value.diverted_weight >= 0 ? value.diverted_weight : 0,
    landfill_weight: value.landfill_weight >= 0 ? value.landfill_weight : 0,
    diversion_rate: value.diversion_rate >= 0 ? value.diversion_rate : 0,
    target_diversion_rate: value.target_diversion_rate >= 0 ? value.target_diversion_rate : 0,
  };
}

/**
 * Type guard for MaterialSummary
 */
export function isMaterialSummary(value: unknown): value is MaterialSummary {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const material = value as any;
  return (
    typeof material.material_type === 'string' &&
    isNumber(material.total_weight) &&
    isNumber(material.diverted_weight) &&
    isNumber(material.landfill_weight) &&
    isNumber(material.diversion_rate)
  );
}

/**
 * Type guard for JobSummaryResponse
 */
export function isJobSummaryResponse(value: unknown): value is JobSummaryResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.summary);
}

/**
 * Ensures a value is valid JobSummaryResponse with safe defaults
 */
export function ensureJobSummaryResponse(value: unknown): JobSummaryResponse {
  const defaultResponse: JobSummaryResponse = {
    summary: [],
  };

  if (!isJobSummaryResponse(value)) {
    console.warn('Invalid JobSummaryResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    summary: Array.isArray(value.summary)
      ? value.summary.filter(isMaterialSummary)
      : [],
  };
}

/**
 * Type guard for County
 */
export function isCounty(value: unknown): value is { id: string; name: string; slug: string; region: string | null } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const county = value as any;
  return (
    typeof county.id === 'string' &&
    typeof county.name === 'string' &&
    typeof county.slug === 'string' &&
    (county.region === null || typeof county.region === 'string')
  );
}

/**
 * Type guard for GetCountiesResponse
 */
export function isGetCountiesResponse(value: unknown): value is { success: boolean; counties: unknown[] } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.success === 'boolean' &&
    Array.isArray(response.counties)
  );
}

/**
 * Ensures a value is valid GetCountiesResponse with safe defaults
 */
export function ensureGetCountiesResponse(value: unknown): { success: boolean; counties: unknown[] } {
  const defaultResponse = {
    success: false,
    counties: [],
  };

  if (!isGetCountiesResponse(value)) {
    console.warn('Invalid GetCountiesResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    success: value.success,
    counties: Array.isArray(value.counties) ? value.counties.filter(isCounty) : [],
  };
}

/**
 * Type guard for ShareLinkResponse
 */
export function isShareLinkResponse(value: unknown): value is { plan_id: string; share_url: string; share_token: string; expires_at: string } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.plan_id === 'string' &&
    typeof response.share_url === 'string' &&
    typeof response.share_token === 'string' &&
    typeof response.expires_at === 'string'
  );
}

/**
 * Ensures a value is valid ShareLinkResponse
 */
export function ensureShareLinkResponse(value: unknown): { plan_id: string; share_url: string; share_token: string; expires_at: string } {
  if (!isShareLinkResponse(value)) {
    console.warn('Invalid ShareLinkResponse:', value);
    throw new Error('Invalid share link response from API');
  }
  return value;
}

/**
 * Type guard for GuestPlanResponse (with plan_data as JSON string)
 */
export function isGuestPlanResponse(value: unknown): value is { plan_data: string } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return typeof response.plan_data === 'string';
}

/**
 * Ensures a value is valid GuestPlanResponse
 */
export function ensureGuestPlanResponse(value: unknown): { plan_data: string } {
  if (!isGuestPlanResponse(value)) {
    console.warn('Invalid GuestPlanResponse:', value);
    throw new Error('Invalid guest plan response from API');
  }
  return value;
}

/**
 * Type guard for MagicLinkValidationResponse
 */
export function isMagicLinkValidationResponse(value: unknown): value is {
  success: boolean;
  token: string;
  expires_at: string;
  provider: {
    id: string;
    email: string;
    company_name: string;
    role: string;
  };
} {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.success === 'boolean' &&
    typeof response.token === 'string' &&
    typeof response.expires_at === 'string' &&
    isPlainObject(response.provider) &&
    typeof response.provider.id === 'string' &&
    typeof response.provider.email === 'string' &&
    typeof response.provider.company_name === 'string' &&
    typeof response.provider.role === 'string'
  );
}

/**
 * Ensures a value is valid MagicLinkValidationResponse
 */
export function ensureMagicLinkValidationResponse(value: unknown): {
  success: boolean;
  token: string;
  expires_at: string;
  provider: {
    id: string;
    email: string;
    company_name: string;
    role: string;
  };
} {
  if (!isMagicLinkValidationResponse(value)) {
    console.warn('Invalid MagicLinkValidationResponse:', value);
    throw new Error('Invalid magic link validation response from API');
  }
  return value;
}

/**
 * Type guard for AvailablePlansResponse
 */
export function isAvailablePlansResponse(value: unknown): value is { plans: unknown[]; total: number } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    Array.isArray(response.plans) &&
    isNumber(response.total)
  );
}

/**
 * Ensures a value is valid AvailablePlansResponse with safe defaults
 */
export function ensureAvailablePlansResponse(value: unknown): { plans: unknown[]; total: number } {
  const defaultResponse = {
    plans: [],
    total: 0,
  };

  if (!isAvailablePlansResponse(value)) {
    console.warn('Invalid AvailablePlansResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    plans: Array.isArray(value.plans) ? value.plans : [],
    total: value.total >= 0 ? value.total : 0,
  };
}

/**
 * Type guard for BidsResponse
 */
export function isBidsResponse(value: unknown): value is { bids: unknown[]; total: number } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    Array.isArray(response.bids) &&
    isNumber(response.total)
  );
}

/**
 * Ensures a value is valid BidsResponse with safe defaults
 */
export function ensureBidsResponse(value: unknown): { bids: unknown[]; total: number } {
  const defaultResponse = {
    bids: [],
    total: 0,
  };

  if (!isBidsResponse(value)) {
    console.warn('Invalid BidsResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    bids: Array.isArray(value.bids) ? value.bids : [],
    total: value.total >= 0 ? value.total : 0,
  };
}

/**
 * Type guard for SubscriptionResponse
 */
export function isSubscriptionResponse(value: unknown): value is { subscription: unknown } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isPlainObject(response.subscription);
}

/**
 * Ensures a value is valid SubscriptionResponse
 */
export function ensureSubscriptionResponse(value: unknown): { subscription: unknown } {
  if (!isSubscriptionResponse(value)) {
    console.warn('Invalid SubscriptionResponse:', value);
    throw new Error('Invalid subscription response from API');
  }
  return value;
}

/**
 * Type guard for BidResponse
 */
export function isBidResponse(value: unknown): value is { bid: unknown } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isPlainObject(response.bid);
}

/**
 * Ensures a value is valid BidResponse
 */
export function ensureBidResponse(value: unknown): { bid: unknown } {
  if (!isBidResponse(value)) {
    console.warn('Invalid BidResponse:', value);
    throw new Error('Invalid bid response from API');
  }
  return value;
}

/**
 * Invoice Service Type Guards
 */

// Import invoice types
import type {
  Invoice,
  InvoiceLineItem,
  InvoiceSummary,
  FinalPaymentResponse,
} from '@/services/invoice.service';

/**
 * Type guard for InvoiceLineItem
 */
export function isInvoiceLineItem(value: unknown): value is InvoiceLineItem {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const item = value as any;
  return (
    typeof item.material_type === 'string' &&
    isNumber(item.quantity) &&
    isNumber(item.unit_price_cents) &&
    isNumber(item.total_cents) &&
    typeof item.is_diverted === 'boolean' &&
    Array.isArray(item.ticket_ids)
  );
}

/**
 * Type guard for Invoice
 */
export function isInvoice(value: unknown): value is Invoice {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const invoice = value as any;
  return (
    typeof invoice.id === 'string' &&
    typeof invoice.job_id === 'string' &&
    Array.isArray(invoice.ticket_ids) &&
    isNumber(invoice.total_amount_cents) &&
    Array.isArray(invoice.line_items) &&
    typeof invoice.status === 'string' &&
    typeof invoice.is_partial === 'boolean' &&
    isNumber(invoice.created_at)
  );
}

/**
 * Type guard for InvoiceResponse
 */
export function isInvoiceResponse(value: unknown): value is { invoice: Invoice } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isInvoice(response.invoice);
}

/**
 * Ensure Invoice response with safe default
 */
export function ensureInvoiceResponse(value: unknown): Invoice {
  const defaultInvoice: Invoice = {
    id: '',
    job_id: '',
    invoice_number: null,
    ticket_ids: [],
    total_amount_cents: 0,
    line_items: [],
    status: 'draft',
    payment_id: null,
    is_partial: false,
    notes: null,
    created_by_provider_id: null,
    created_at: Date.now(),
    sent_at: null,
    paid_at: null,
  };

  if (!isInvoiceResponse(value)) {
    console.warn('Invalid InvoiceResponse:', value);
    return defaultInvoice;
  }

  return value.invoice;
}

/**
 * Type guard for InvoicesListResponse
 */
export function isInvoicesListResponse(value: unknown): value is { invoices: Invoice[] } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.invoices);
}

/**
 * Ensure InvoicesList response with safe defaults
 */
export function ensureInvoicesListResponse(value: unknown): Invoice[] {
  if (!isInvoicesListResponse(value)) {
    console.warn('Invalid InvoicesListResponse:', value);
    return [];
  }

  return value.invoices.filter(isInvoice);
}

/**
 * Type guard for InvoiceSummary
 */
export function isInvoiceSummary(value: unknown): value is InvoiceSummary {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const summary = value as any;
  return (
    isInvoice(summary.invoice) &&
    isPlainObject(summary.job) &&
    isPlainObject(summary.service_provider) &&
    isNumber(summary.balance_due_cents)
  );
}

/**
 * Ensure InvoiceSummary with safe defaults
 */
export function ensureInvoiceSummary(value: unknown): InvoiceSummary {
  const defaultSummary: InvoiceSummary = {
    invoice: {
      id: '',
      job_id: '',
      invoice_number: null,
      ticket_ids: [],
      total_amount_cents: 0,
      line_items: [],
      status: 'draft',
      payment_id: null,
      is_partial: false,
      notes: null,
      created_by_provider_id: null,
      created_at: Date.now(),
      sent_at: null,
      paid_at: null,
    },
    job: {
      project_name: '',
      applicant_email: '',
    },
    service_provider: {
      name: '',
      contact_email: '',
    },
    balance_due_cents: 0,
  };

  if (!isInvoiceSummary(value)) {
    console.warn('Invalid InvoiceSummary:', value);
    return defaultSummary;
  }

  return value;
}

/**
 * Type guard for FinalPaymentResponse
 */
export function isFinalPaymentResponse(value: unknown): value is FinalPaymentResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.payment_intent_id === 'string' &&
    isNumber(response.amount_cents)
  );
}

/**
 * Ensure FinalPaymentResponse with safe defaults
 */
export function ensureFinalPaymentResponse(value: unknown): FinalPaymentResponse {
  const defaultResponse: FinalPaymentResponse = {
    payment_intent_id: '',
    client_secret: null,
    amount_cents: 0,
  };

  if (!isFinalPaymentResponse(value)) {
    console.warn('Invalid FinalPaymentResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Subscription Service Type Guards
 */

import type {
  CheckoutSessionResponse,
  StripeOnboardingResponse,
  StripeAccountStatus,
  CancelSubscriptionResponse,
  InvoicesResponse,
} from '@/services/subscription.service';
import type { Subscription } from '@/services/service-provider.service';

/**
 * Type guard for Subscription
 */
export function isSubscription(value: unknown): value is Subscription {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sub = value as any;
  return (
    typeof sub.id === 'string' &&
    typeof sub.service_provider_id === 'string' &&
    typeof sub.tier === 'string' &&
    typeof sub.status === 'string' &&
    isNumber(sub.transaction_fee_percent) &&
    isNumber(sub.current_period_start) &&
    isNumber(sub.current_period_end) &&
    isNumber(sub.cancel_at_period_end)
  );
}

/**
 * Ensure Subscription with better validation
 */
export function ensureSubscription(value: unknown): Subscription {
  const defaultSubscription: Subscription = {
    id: '',
    service_provider_id: '',
    tier: 'free',
    status: 'active',
    stripe_subscription_id: null,
    stripe_customer_id: null,
    service_area_counties: '[]',
    monthly_job_limit: null,
    transaction_fee_percent: 0,
    current_period_start: Date.now(),
    current_period_end: Date.now(),
    cancel_at_period_end: 0,
    created_at: Date.now(),
    updated_at: Date.now(),
  };

  if (!isSubscription(value)) {
    console.warn('Invalid Subscription:', value);
    return defaultSubscription;
  }

  return value;
}

/**
 * Type guard for CheckoutSessionResponse
 */
export function isCheckoutSessionResponse(value: unknown): value is CheckoutSessionResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.sessionId === 'string' &&
    typeof response.url === 'string'
  );
}

/**
 * Ensure CheckoutSessionResponse with safe defaults
 */
export function ensureCheckoutSessionResponse(value: unknown): CheckoutSessionResponse {
  const defaultResponse: CheckoutSessionResponse = {
    sessionId: '',
    url: '',
  };

  if (!isCheckoutSessionResponse(value)) {
    console.warn('Invalid CheckoutSessionResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for StripeOnboardingResponse
 */
export function isStripeOnboardingResponse(value: unknown): value is StripeOnboardingResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.accountId === 'string' &&
    typeof response.onboardingUrl === 'string'
  );
}

/**
 * Ensure StripeOnboardingResponse with safe defaults
 */
export function ensureStripeOnboardingResponse(value: unknown): StripeOnboardingResponse {
  const defaultResponse: StripeOnboardingResponse = {
    accountId: '',
    onboardingUrl: '',
  };

  if (!isStripeOnboardingResponse(value)) {
    console.warn('Invalid StripeOnboardingResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for StripeAccountStatus
 */
export function isStripeAccountStatus(value: unknown): value is StripeAccountStatus {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status = value as any;
  return (
    typeof status.charges_enabled === 'boolean' &&
    typeof status.details_submitted === 'boolean' &&
    typeof status.payouts_enabled === 'boolean'
  );
}

/**
 * Ensure StripeAccountStatus with safe defaults
 */
export function ensureStripeAccountStatus(value: unknown): StripeAccountStatus {
  const defaultStatus: StripeAccountStatus = {
    charges_enabled: false,
    details_submitted: false,
    payouts_enabled: false,
    requirements: {
      currently_due: [],
      eventually_due: [],
      past_due: [],
    },
  };

  if (!isStripeAccountStatus(value)) {
    console.warn('Invalid StripeAccountStatus:', value);
    return defaultStatus;
  }

  return value;
}

/**
 * Type guard for CancelSubscriptionResponse
 */
export function isCancelSubscriptionResponse(value: unknown): value is CancelSubscriptionResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.success === 'boolean' &&
    isNumber(response.cancels_at)
  );
}

/**
 * Ensure CancelSubscriptionResponse with safe defaults
 */
export function ensureCancelSubscriptionResponse(value: unknown): CancelSubscriptionResponse {
  const defaultResponse: CancelSubscriptionResponse = {
    success: false,
    cancels_at: Date.now(),
  };

  if (!isCancelSubscriptionResponse(value)) {
    console.warn('Invalid CancelSubscriptionResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for InvoicesResponse (Stripe invoices)
 */
export function isStripeInvoicesResponse(value: unknown): value is InvoicesResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    Array.isArray(response.invoices) &&
    isNumber(response.total)
  );
}

/**
 * Ensure InvoicesResponse with safe defaults
 */
export function ensureStripeInvoicesResponse(value: unknown): InvoicesResponse {
  const defaultResponse: InvoicesResponse = {
    invoices: [],
    total: 0,
  };

  if (!isStripeInvoicesResponse(value)) {
    console.warn('Invalid InvoicesResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for SetupIntent response
 */
export function isSetupIntentResponse(value: unknown): value is { client_secret: string; setup_intent_id: string } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.client_secret === 'string' &&
    typeof response.setup_intent_id === 'string'
  );
}

/**
 * Ensure SetupIntent response with safe defaults
 */
export function ensureSetupIntentResponse(value: unknown): { client_secret: string; setup_intent_id: string } {
  const defaultResponse = {
    client_secret: '',
    setup_intent_id: '',
  };

  if (!isSetupIntentResponse(value)) {
    console.warn('Invalid SetupIntentResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for PaymentMethod response
 */
export function isPaymentMethodResponse(value: unknown): value is {
  id: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
} {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.id === 'string' &&
    isPlainObject(response.card) &&
    typeof response.card.brand === 'string' &&
    typeof response.card.last4 === 'string' &&
    isNumber(response.card.exp_month) &&
    isNumber(response.card.exp_year)
  );
}

/**
 * Ensure PaymentMethod response with safe defaults
 */
export function ensurePaymentMethodResponse(value: unknown): {
  id: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
} {
  const defaultResponse = {
    id: '',
    card: {
      brand: '',
      last4: '',
      exp_month: 0,
      exp_year: 0,
    },
  };

  if (!isPaymentMethodResponse(value)) {
    console.warn('Invalid PaymentMethodResponse:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Auth Service Type Guards
 */

import type { AuthToken, Applicant } from '@/types';

/**
 * Type guard for AuthToken
 */
export function isAuthToken(value: unknown): value is AuthToken {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const token = value as any;
  return (
    typeof token.token === 'string' &&
    typeof token.expires_at === 'string'
  );
}

/**
 * Ensure AuthToken with validation
 */
export function ensureAuthToken(value: unknown): AuthToken {
  if (!isAuthToken(value)) {
    console.warn('Invalid AuthToken:', value);
    throw new Error('Invalid auth token response');
  }
  return value;
}

/**
 * Type guard for Applicant
 */
export function isApplicant(value: unknown): value is Applicant {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applicant = value as any;
  return (
    typeof applicant.id === 'string' &&
    typeof applicant.email === 'string'
  );
}

/**
 * Ensure Applicant with validation
 */
export function ensureApplicant(value: unknown): Applicant {
  if (!isApplicant(value)) {
    console.warn('Invalid Applicant:', value);
    throw new Error('Invalid applicant response');
  }
  return value;
}

// ========================================
// Marketplace Service Type Guards
// ========================================

import type {
  JobOpportunity,
  JobOpportunitiesResponse,
  Bid,
  BidsResponse as MarketplaceBidsResponse,
  SubmitBidResponse,
  PostJobOpportunityResponse,
  AcceptBidResponse as MarketplaceAcceptBidResponse,
  RejectBidResponse,
  WithdrawBidResponse,
  AwardBidResponse,
  CancelJobOpportunityResponse,
  BidPricing,
} from '@/types/marketplace';

/**
 * Type guard for BidPricing
 */
export function isBidPricing(value: unknown): value is BidPricing {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pricing = value as any;
  return (
    isNumber(pricing.basePrice) &&
    isNumber(pricing.estimatedTotal) &&
    (pricing.perTonPrice === undefined || isNumber(pricing.perTonPrice))
  );
}

/**
 * Type guard for JobOpportunity
 */
export function isJobOpportunity(value: unknown): value is JobOpportunity {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const job = value as any;
  return (
    typeof job.id === 'string' &&
    typeof job.planId === 'string' &&
    typeof job.projectName === 'string' &&
    typeof job.projectAddress === 'string' &&
    isNumber(job.targetDiversionRate) &&
    isNumber(job.startDate) &&
    typeof job.status === 'string' &&
    isNumber(job.createdAt) &&
    isNumber(job.updatedAt)
  );
}

/**
 * Type guard for MarketplaceBid
 */
export function isMarketplaceBid(value: unknown): value is Bid {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bid = value as any;
  return (
    typeof bid.id === 'string' &&
    typeof bid.jobId === 'string' &&
    typeof bid.planId === 'string' &&
    typeof bid.serviceProviderId === 'string' &&
    isBidPricing(bid.pricing) &&
    typeof bid.serviceDescription === 'string' &&
    typeof bid.status === 'string' &&
    isNumber(bid.createdAt) &&
    isNumber(bid.updatedAt)
  );
}

/**
 * Type guard for JobOpportunitiesResponse
 */
export function isJobOpportunitiesResponse(value: unknown): value is JobOpportunitiesResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    Array.isArray(response.jobs) &&
    isNumber(response.total) &&
    isNumber(response.limit) &&
    isNumber(response.offset)
  );
}

/**
 * Ensures a value is valid JobOpportunitiesResponse with safe defaults
 */
export function ensureJobOpportunitiesResponse(value: unknown): JobOpportunitiesResponse {
  const defaultResponse: JobOpportunitiesResponse = {
    jobs: [],
    total: 0,
    limit: 50,
    offset: 0,
  };

  if (!isJobOpportunitiesResponse(value)) {
    console.warn('Invalid JobOpportunitiesResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    jobs: Array.isArray(value.jobs) ? value.jobs.filter(isJobOpportunity) : [],
    total: value.total >= 0 ? value.total : 0,
    limit: value.limit > 0 ? value.limit : 50,
    offset: value.offset >= 0 ? value.offset : 0,
  };
}

/**
 * Ensures a value is valid JobOpportunity with validation
 */
export function ensureJobOpportunity(value: unknown): JobOpportunity {
  if (!isJobOpportunity(value)) {
    console.warn('Invalid JobOpportunity:', value);
    throw new Error('Invalid job opportunity response');
  }
  return value;
}

/**
 * Type guard for MarketplaceBidsResponse
 */
export function isMarketplaceBidsResponse(value: unknown): value is MarketplaceBidsResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    Array.isArray(response.bids) &&
    isNumber(response.total) &&
    isNumber(response.limit) &&
    isNumber(response.offset)
  );
}

/**
 * Ensures a value is valid MarketplaceBidsResponse with safe defaults
 */
export function ensureMarketplaceBidsResponse(value: unknown): MarketplaceBidsResponse {
  const defaultResponse: MarketplaceBidsResponse = {
    bids: [],
    total: 0,
    limit: 100,
    offset: 0,
  };

  if (!isMarketplaceBidsResponse(value)) {
    console.warn('Invalid MarketplaceBidsResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    bids: Array.isArray(value.bids) ? value.bids.filter(isMarketplaceBid) : [],
    total: value.total >= 0 ? value.total : 0,
    limit: value.limit > 0 ? value.limit : 100,
    offset: value.offset >= 0 ? value.offset : 0,
  };
}

/**
 * Type guard for SubmitBidResponse
 */
export function isSubmitBidResponse(value: unknown): value is SubmitBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.bidId === 'string' &&
    typeof response.jobId === 'string' &&
    response.status === 'pending' &&
    isNumber(response.submittedAt)
  );
}

/**
 * Ensures a value is valid SubmitBidResponse with validation
 */
export function ensureSubmitBidResponse(value: unknown): SubmitBidResponse {
  if (!isSubmitBidResponse(value)) {
    console.warn('Invalid SubmitBidResponse:', value);
    throw new Error('Invalid submit bid response');
  }
  return value;
}

/**
 * Type guard for PostJobOpportunityResponse
 */
export function isPostJobOpportunityResponse(value: unknown): value is PostJobOpportunityResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.jobId === 'string' &&
    typeof response.planId === 'string' &&
    typeof response.projectName === 'string' &&
    response.status === 'active' &&
    isNumber(response.postedAt)
  );
}

/**
 * Ensures a value is valid PostJobOpportunityResponse with validation
 */
export function ensurePostJobOpportunityResponse(value: unknown): PostJobOpportunityResponse {
  if (!isPostJobOpportunityResponse(value)) {
    console.warn('Invalid PostJobOpportunityResponse:', value);
    throw new Error('Invalid post job opportunity response');
  }
  return value;
}

/**
 * Type guard for MarketplaceAcceptBidResponse
 */
export function isMarketplaceAcceptBidResponse(value: unknown): value is MarketplaceAcceptBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.bidId === 'string' &&
    typeof response.jobId === 'string' &&
    response.status === 'accepted' &&
    isNumber(response.acceptedAt) &&
    typeof response.providerId === 'string'
  );
}

/**
 * Ensures a value is valid MarketplaceAcceptBidResponse with validation
 */
export function ensureMarketplaceAcceptBidResponse(value: unknown): MarketplaceAcceptBidResponse {
  if (!isMarketplaceAcceptBidResponse(value)) {
    console.warn('Invalid MarketplaceAcceptBidResponse:', value);
    throw new Error('Invalid accept bid response');
  }
  return value;
}

/**
 * Type guard for RejectBidResponse
 */
export function isRejectBidResponse(value: unknown): value is RejectBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.bidId === 'string' &&
    response.status === 'rejected' &&
    isNumber(response.rejectedAt) &&
    typeof response.reason === 'string'
  );
}

/**
 * Ensures a value is valid RejectBidResponse with validation
 */
export function ensureRejectBidResponse(value: unknown): RejectBidResponse {
  if (!isRejectBidResponse(value)) {
    console.warn('Invalid RejectBidResponse:', value);
    throw new Error('Invalid reject bid response');
  }
  return value;
}

/**
 * Type guard for WithdrawBidResponse
 */
export function isWithdrawBidResponse(value: unknown): value is WithdrawBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.bidId === 'string' &&
    response.status === 'withdrawn' &&
    isNumber(response.withdrawnAt)
  );
}

/**
 * Ensures a value is valid WithdrawBidResponse with validation
 */
export function ensureWithdrawBidResponse(value: unknown): WithdrawBidResponse {
  if (!isWithdrawBidResponse(value)) {
    console.warn('Invalid WithdrawBidResponse:', value);
    throw new Error('Invalid withdraw bid response');
  }
  return value;
}

/**
 * Type guard for AwardBidResponse
 */
export function isAwardBidResponse(value: unknown): value is AwardBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.bidId === 'string' &&
    typeof response.jobId === 'string' &&
    typeof response.providerId === 'string' &&
    response.status === 'awarded' &&
    isNumber(response.awardedAt)
  );
}

/**
 * Ensures a value is valid AwardBidResponse with validation
 */
export function ensureAwardBidResponse(value: unknown): AwardBidResponse {
  if (!isAwardBidResponse(value)) {
    console.warn('Invalid AwardBidResponse:', value);
    throw new Error('Invalid award bid response');
  }
  return value;
}

/**
 * Type guard for CancelJobOpportunityResponse
 */
export function isCancelJobOpportunityResponse(value: unknown): value is CancelJobOpportunityResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.jobId === 'string' &&
    response.status === 'cancelled' &&
    isNumber(response.cancelledAt)
  );
}

/**
 * Ensures a value is valid CancelJobOpportunityResponse with validation
 */
export function ensureCancelJobOpportunityResponse(value: unknown): CancelJobOpportunityResponse {
  if (!isCancelJobOpportunityResponse(value)) {
    console.warn('Invalid CancelJobOpportunityResponse:', value);
    throw new Error('Invalid cancel job opportunity response');
  }
  return value;
}

/**
 * Type guard for Bid array response from getJobBids
 */
export function isMarketplaceBidArrayResponse(value: unknown): value is { bids: Bid[] } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.bids);
}

/**
 * Ensures a value is valid MarketplaceBid array response with safe defaults
 */
export function ensureMarketplaceBidArrayResponse(value: unknown): { bids: Bid[] } {
  const defaultResponse = {
    bids: [],
  };

  if (!isMarketplaceBidArrayResponse(value)) {
    console.warn('Invalid MarketplaceBid array response, using defaults:', value);
    return defaultResponse;
  }

  return {
    bids: Array.isArray(value.bids) ? value.bids.filter(isMarketplaceBid) : [],
  };
}

// ========================================
// Plan Service Type Guards
// ========================================

import type { Plan, PlanBidWithPricing, AcceptBidResponse } from '@/types';

/**
 * Type guard for Plan
 * Note: Backend returns dates as ISO strings, not numbers
 */
export function isPlan(value: unknown): value is Plan {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plan = value as any;
  return (
    typeof plan.id === 'string' &&
    typeof plan.plan_data === 'string' &&
    typeof plan.status === 'string' &&
    (isNumber(plan.created_at) || typeof plan.created_at === 'string') &&
    (isNumber(plan.updated_at) || typeof plan.updated_at === 'string')
  );
}

/**
 * Type guard for Plan array
 */
export function isPlanArray(value: unknown): value is Plan[] {
  return Array.isArray(value) && value.every(isPlan);
}

/**
 * Ensures a value is valid Plan array with safe defaults
 */
export function ensurePlanArray(value: unknown): Plan[] {
  if (!isPlanArray(value)) {
    console.warn('Invalid Plan array, using defaults:', value);
    return [];
  }
  return value;
}

/**
 * Type guard for Plan response
 */
export function isPlanResponse(value: unknown): value is { plan: Plan } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isPlan(response.plan);
}

/**
 * Ensures a value is valid Plan response with validation
 */
export function ensurePlanResponse(value: unknown): { plan: Plan } {
  if (!isPlanResponse(value)) {
    console.warn('Invalid Plan response:', value);
    throw new Error('Invalid plan response');
  }
  return value;
}

/**
 * Type guard for CreatePlanResponse
 */
export function isCreatePlanResponse(value: unknown): value is { plan: Plan; plan_url: string } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isPlan(response.plan) && typeof response.plan_url === 'string';
}

/**
 * Ensures a value is valid CreatePlanResponse with validation
 */
export function ensureCreatePlanResponse(value: unknown): { plan: Plan; plan_url: string } {
  if (!isCreatePlanResponse(value)) {
    console.warn('Invalid CreatePlanResponse:', value);
    throw new Error('Invalid create plan response');
  }
  return value;
}

/**
 * Type guard for PlanBidWithPricing
 */
export function isPlanBidWithPricing(value: unknown): value is PlanBidWithPricing {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bid = value as any;
  return (
    typeof bid.id === 'string' &&
    typeof bid.plan_id === 'string' &&
    typeof bid.service_provider_id === 'string' &&
    typeof bid.status === 'string' &&
    isNumber(bid.created_at)
  );
}

/**
 * Type guard for GetPlanBidsResponse
 */
export function isGetPlanBidsResponse(value: unknown): value is { bids: PlanBidWithPricing[]; total: number } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.bids) && isNumber(response.total);
}

/**
 * Ensures a value is valid GetPlanBidsResponse with safe defaults
 */
export function ensureGetPlanBidsResponse(value: unknown): { bids: PlanBidWithPricing[]; total: number } {
  const defaultResponse = {
    bids: [],
    total: 0,
  };

  if (!isGetPlanBidsResponse(value)) {
    console.warn('Invalid GetPlanBidsResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    bids: Array.isArray(value.bids) ? value.bids.filter(isPlanBidWithPricing) : [],
    total: value.total >= 0 ? value.total : 0,
  };
}

/**
 * Type guard for Plan AcceptBidResponse
 */
export function isPlanAcceptBidResponse(value: unknown): value is AcceptBidResponse {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return (
    typeof response.success === 'boolean' &&
    typeof response.payment_client_secret === 'string' &&
    typeof response.bid_id === 'string' &&
    isNumber(response.deposit_amount_cents)
  );
}

/**
 * Ensures a value is valid Plan AcceptBidResponse with validation
 */
export function ensurePlanAcceptBidResponse(value: unknown): AcceptBidResponse {
  if (!isPlanAcceptBidResponse(value)) {
    console.warn('Invalid Plan AcceptBidResponse:', value);
    throw new Error('Invalid accept bid response');
  }
  return value;
}

/**
 * Type guard for PlanBidActionResponse
 */
export function isPlanBidActionResponse(value: unknown): value is { bid: PlanBidWithPricing } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isPlanBidWithPricing(response.bid);
}

/**
 * Ensures a value is valid PlanBidActionResponse with validation
 */
export function ensurePlanBidActionResponse(value: unknown): { bid: PlanBidWithPricing } {
  if (!isPlanBidActionResponse(value)) {
    console.warn('Invalid PlanBidActionResponse:', value);
    throw new Error('Invalid bid action response');
  }
  return value;
}

// ========================================
// Job Service Type Guards
// ========================================

import type { Job, JobStats, MaterialTicket } from '@/services/job.service';

/**
 * Type guard for Job
 */
export function isJob(value: unknown): value is Job {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const job = value as any;
  return (
    typeof job.id === 'string' &&
    typeof job.plan_id === 'string' &&
    typeof job.status === 'string' &&
    typeof job.project_name === 'string' &&
    typeof job.project_address === 'string' &&
    isNumber(job.start_date) &&
    isNumber(job.target_diversion_rate) &&
    isNumber(job.created_at) &&
    isNumber(job.updated_at) &&
    isNumber(job.last_accessed_at)
  );
}

/**
 * Type guard for JobResponse
 */
export function isJobResponse(value: unknown): value is { job: Job } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isJob(response.job);
}

/**
 * Ensures a value is valid JobResponse with validation
 */
export function ensureJobResponse(value: unknown): { job: Job } {
  if (!isJobResponse(value)) {
    console.warn('Invalid JobResponse:', value);
    throw new Error('Invalid job response');
  }
  return value;
}

/**
 * Type guard for JobStats
 */
export function isJobStats(value: unknown): value is JobStats {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stats = value as any;
  return (
    isNumber(stats.total_weight) &&
    isNumber(stats.diverted_weight) &&
    isNumber(stats.landfill_weight) &&
    isNumber(stats.diversion_rate) &&
    isNumber(stats.ticket_count) &&
    Array.isArray(stats.material_summary)
  );
}

/**
 * Type guard for JobStatsResponse
 */
export function isJobStatsResponse(value: unknown): value is { stats: JobStats } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isJobStats(response.stats);
}

/**
 * Ensures a value is valid JobStatsResponse with safe defaults
 */
export function ensureJobStatsResponse(value: unknown): { stats: JobStats } {
  const defaultResponse = {
    stats: {
      total_weight: 0,
      diverted_weight: 0,
      landfill_weight: 0,
      diversion_rate: 0,
      ticket_count: 0,
      material_summary: [],
    },
  };

  if (!isJobStatsResponse(value)) {
    console.warn('Invalid JobStatsResponse, using defaults:', value);
    return defaultResponse;
  }

  return value;
}

/**
 * Type guard for MaterialTicket
 */
export function isMaterialTicket(value: unknown): value is MaterialTicket {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ticket = value as any;
  return (
    typeof ticket.id === 'string' &&
    typeof ticket.job_id === 'string' &&
    isNumber(ticket.ticket_date) &&
    typeof ticket.hauler_name === 'string' &&
    typeof ticket.facility_name === 'string' &&
    typeof ticket.destination_type === 'string' &&
    Array.isArray(ticket.materials) &&
    isNumber(ticket.created_at) &&
    isNumber(ticket.updated_at)
  );
}

/**
 * Type guard for MaterialTicketsResponse
 */
export function isMaterialTicketsResponse(value: unknown): value is { tickets: MaterialTicket[] } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return Array.isArray(response.tickets);
}

/**
 * Ensures a value is valid MaterialTicketsResponse with safe defaults
 */
export function ensureMaterialTicketsResponse(value: unknown): { tickets: MaterialTicket[] } {
  const defaultResponse = {
    tickets: [],
  };

  if (!isMaterialTicketsResponse(value)) {
    console.warn('Invalid MaterialTicketsResponse, using defaults:', value);
    return defaultResponse;
  }

  return {
    tickets: Array.isArray(value.tickets) ? value.tickets.filter(isMaterialTicket) : [],
  };
}

/**
 * Type guard for MaterialTicket response
 */
export function isMaterialTicketResponse(value: unknown): value is { ticket: MaterialTicket } {
  if (!isPlainObject(value)) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = value as any;
  return isMaterialTicket(response.ticket);
}

/**
 * Ensures a value is valid MaterialTicket response with validation
 */
export function ensureMaterialTicketResponse(value: unknown): { ticket: MaterialTicket } {
  if (!isMaterialTicketResponse(value)) {
    console.warn('Invalid MaterialTicket response:', value);
    throw new Error('Invalid material ticket response');
  }
  return value;
}
